import type { ReactElement } from 'react';
import type { ICheckedTreatment } from '@common/interfaces/checkedTreatment';
import type { IDiagnosticReport } from './AdminProcessDetails';
import type { AdminProcess } from './adminProcessDetailsTypes';
import { ProcedureStatus } from '../../../../common/enums/procedureStatus';
import { ReportCategories } from '../../../../common/enums/reportCategories';
import { RequestStatus } from '../../../../common/enums/requestStatus';
import { ServiceType } from '../../../../common/enums/serviceType';
import { TaskStatus, TaskType } from '../../../../common/enums/task';
import { translateKey } from '../../../translate';
import { FormLabel } from '../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../elements/forms/HorizontalFormGroup';
import { PractitionerAndOrganizationActors } from './PractitionerAndOrganizationActors';

interface IAdminProcessDetailsActorsProps {
    adminProcess: AdminProcess;
}

export function AdminProcessDetailsActors(props: IAdminProcessDetailsActorsProps) {
    return (
        <>
            {props.adminProcess.service.serviceType === ServiceType.examination && (
                <>
                    {requestRequester(translateKey('processDetails.requestedBy'))}

                    {/* In txcs it was possible to send processes to a zorgmail adddress and to not use a practitioner and organization account */}
                    {props.adminProcess.txcsProcesId &&
                        props.adminProcess.customControlData?.zorgmailRequesterName &&
                        requestRequester(
                            translateKey('processDetails.zorgmailGeneralPractitioner'),
                            props.adminProcess.customControlData.zorgmailRequesterName +
                                ' - AGB:' +
                                props.adminProcess.customControlData.zorgmailRequesterAgbcode
                        )}

                    {performerOrRecipientProcedure(translateKey('processDetails.examinedBy'))}
                    {performerOrRecipientDiagnosticReport(translateKey('processDetails.gradedBy'))}
                    {performerSupervision(translateKey('processDetails.supervisedBy'))}
                </>
            )}

            {props.adminProcess.service.serviceType === ServiceType.consultation && (
                <>
                    {requestRequester(translateKey('processDetails.consultBy'))}
                    {performerOrRecipientDiagnosticReport(translateKey('processDetails.answeredBy'))}
                </>
            )}

            {props.adminProcess.service.serviceType === ServiceType.referral && (
                <>
                    {requestRequester(translateKey('processDetails.referredBy'))}
                    {performerOrRecipientReferral(translateKey('request.sentTo'))}
                    {performerOrRecipientIntakeOrDischargeReport(
                        translateKey('processDetails.intakeBy'),
                        ReportCategories.intake
                    )}
                    {performerOrRecipientIntakeOrDischargeReport(
                        translateKey('processDetails.dischargeBy'),
                        ReportCategories.discharge
                    )}
                </>
            )}

            {props.adminProcess.service.serviceType === ServiceType.referralSmall && (
                <>
                    {requestRequester(translateKey('processDetails.referredBy'))}
                    {performerOrRecipientReferral(translateKey('request.sentTo'))}
                </>
            )}

            {props.adminProcess.service.serviceType === ServiceType.contact && (
                <>{requestRequester(translateKey('processDetails.contactBy'))}</>
            )}

            {props.adminProcess.service.serviceType === ServiceType.invitation && (
                <>
                    {performerOrRecipientProcedure(translateKey('processDetails.counselor'))}
                    {patientGeneralPractitioner(translateKey('professionEnum.generalPractitioner'))}
                </>
            )}

            <HorizontalFormGroup>
                <FormLabel>{translateKey('carePath.txcsId')}</FormLabel>
                <FormLabel>
                    {props.adminProcess.txcsProcesId
                        ? props.adminProcess.txcsProcesId
                        : translateKey('common.notMigrated')}
                </FormLabel>
            </HorizontalFormGroup>

            {props.adminProcess.service.serviceType === ServiceType.invitation &&
                props.adminProcess.procedure?.customControlData.id && (
                    <>{invitationTherapieLandId(props.adminProcess.procedure?.customControlData.id)}</>
                )}

            {props.adminProcess.service.serviceType === ServiceType.counseling &&
                props.adminProcess.customControlData?.checkedTreatments?.length > 0 && (
                    <>
                        {invitationTherapielandIdsForCounseling(
                            props.adminProcess.customControlData?.checkedTreatments
                        )}
                    </>
                )}
        </>
    );

    function invitationTherapielandIdsForCounseling(checkedTreatments: ICheckedTreatment[]): ReactElement<any> {
        const checkedTreatmentsRows = checkedTreatments
            .sort((a, b) => a.id - b.id)
            .map((treatment) => <div key={treatment.id}>{treatment.id}</div>);

        return (
            <HorizontalFormGroup>
                <FormLabel>{translateKey('processDetails.therapielandIdFieldLabel')}</FormLabel>
                <FormLabel>{checkedTreatmentsRows}</FormLabel>
            </HorizontalFormGroup>
        );
    }

    function invitationTherapieLandId(id: number): ReactElement<any> {
        return (
            <HorizontalFormGroup>
                <FormLabel>{translateKey('processDetails.therapielandIdFieldLabel')}</FormLabel>
                <FormLabel>{id}</FormLabel>
            </HorizontalFormGroup>
        );
    }

    function requestRequester(label: string, alternativeText?: string) {
        return (
            <PractitionerAndOrganizationActors
                adminProcess={props.adminProcess}
                alternativeText={alternativeText}
                entity="request"
                label={label}
                organization={props.adminProcess.requesterOrganization}
                organizationId={props.adminProcess.requesterOrganizationId}
                practitioner={props.adminProcess.requesterPractitioner}
                practitionerId={props.adminProcess.requesterPractitionerId}
            />
        );
    }

    function performerOrRecipientProcedure(label: string) {
        if (
            !props.adminProcess.procedure &&
            (props.adminProcess.status === RequestStatus.accepted ||
                props.adminProcess.status === RequestStatus.requested)
        ) {
            return (
                <PractitionerAndOrganizationActors
                    adminProcess={props.adminProcess}
                    entity="procedure"
                    label={label}
                    organization={props.adminProcess.recipientOrganization}
                    organizationId={props.adminProcess.recipientOrganizationId}
                    practitioner={props.adminProcess.recipientPractitioner}
                    practitionerId={props.adminProcess.recipientPractitionerId}
                />
            );
        }

        if (!props.adminProcess.procedure) {
            return null;
        }

        return (
            <PractitionerAndOrganizationActors
                adminProcess={props.adminProcess}
                entity="procedure"
                label={label}
                organization={props.adminProcess.procedure.performerOrganization}
                organizationId={props.adminProcess.procedure.performerOrganizationId}
                practitioner={props.adminProcess.procedure.performerPractitioner}
                practitionerId={props.adminProcess.procedure.performerPractitionerId}
            />
        );
    }

    function performerOrRecipientReferral(label: string) {
        if (
            props.adminProcess.status !== RequestStatus.requested &&
            props.adminProcess.status !== RequestStatus.accepted
        ) {
            return null;
        }

        return (
            <PractitionerAndOrganizationActors
                adminProcess={props.adminProcess}
                entity="diagnosticReport"
                label={label}
                organization={props.adminProcess.recipientOrganization}
                organizationId={props.adminProcess.recipientOrganizationId}
                practitioner={props.adminProcess.recipientPractitioner}
                practitionerId={props.adminProcess.recipientPractitionerId}
            />
        );
    }

    function performerOrRecipientIntakeOrDischargeReport(
        label: string,
        category: ReportCategories.intake | ReportCategories.discharge
    ) {
        if (props.adminProcess.diagnosticReports && props.adminProcess.diagnosticReports.length > 0) {
            const report = props.adminProcess.diagnosticReports.find((dp) => dp.category === category);
            if (report) {
                return (
                    <PractitionerAndOrganizationActors
                        adminProcess={props.adminProcess}
                        entity="diagnosticReport"
                        label={label}
                        organization={report.performerOrganization}
                        organizationId={report.performerOrganizationId}
                        practitioner={report.performerPractitioner}
                        practitionerId={report.performerPractitionerId}
                    />
                );
            } else {
                return (
                    <PractitionerAndOrganizationActors
                        adminProcess={props.adminProcess}
                        entity="diagnosticReport"
                        label={label}
                    />
                );
            }
        }

        return null;
    }

    function performerOrRecipientDiagnosticReport(label: string) {
        let lastDiagnosticReport: IDiagnosticReport | undefined;
        if (props.adminProcess.diagnosticReports && props.adminProcess.diagnosticReports.length > 0) {
            lastDiagnosticReport =
                props.adminProcess.diagnosticReports[props.adminProcess.diagnosticReports.length - 1];
        }

        if (
            !lastDiagnosticReport &&
            ((props.adminProcess.service.serviceType === ServiceType.examination &&
                props.adminProcess.procedure &&
                props.adminProcess.procedure.status === ProcedureStatus.completed) ||
                (props.adminProcess.service.serviceType === ServiceType.consultation &&
                    (props.adminProcess.status === RequestStatus.accepted ||
                        props.adminProcess.status === RequestStatus.requested)))
        ) {
            return (
                <PractitionerAndOrganizationActors
                    adminProcess={props.adminProcess}
                    entity="diagnosticReport"
                    label={label}
                    organization={props.adminProcess.recipientOrganization}
                    organizationId={props.adminProcess.recipientOrganizationId}
                    practitioner={props.adminProcess.recipientPractitioner}
                    practitionerId={props.adminProcess.recipientPractitionerId}
                />
            );
        }

        if (!lastDiagnosticReport) {
            return null;
        }

        return (
            <PractitionerAndOrganizationActors
                adminProcess={props.adminProcess}
                entity="diagnosticReport"
                label={label}
                organization={lastDiagnosticReport.performerOrganization}
                organizationId={lastDiagnosticReport.performerOrganizationId}
                practitioner={lastDiagnosticReport.performerPractitioner}
                practitionerId={lastDiagnosticReport.performerPractitionerId}
            />
        );
    }

    function performerSupervision(label: string) {
        const supervisionTask = props.adminProcess.tasks?.find(
            (task) =>
                task.type === TaskType.supervise &&
                task.status !== TaskStatus.cancelled &&
                task.performerOrganizationId &&
                task.performerPractitionerId
        );
        if (!supervisionTask) {
            return null;
        }

        return (
            <PractitionerAndOrganizationActors
                adminProcess={props.adminProcess}
                entity="supervision"
                label={label}
                organization={supervisionTask.performerOrganization}
                organizationId={supervisionTask.performerOrganizationId}
                practitioner={supervisionTask.performerPractitioner}
                practitionerId={supervisionTask.performerPractitionerId}
            />
        );
    }

    function patientGeneralPractitioner(label: string) {
        return (
            <PractitionerAndOrganizationActors
                adminProcess={props.adminProcess}
                entity="request"
                label={label}
                organization={props.adminProcess.patient.careProviderOrganization}
                organizationId={props.adminProcess.patient.careProviderOrganizationId}
                practitioner={props.adminProcess.patient.careProviderPractitioner}
                practitionerId={props.adminProcess.patient.careProviderPractitionerId}
            />
        );
    }
}
